"use client"

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { BookOpen, Loader2, AlertCircle, CheckCircle2, ArrowLeft } from 'lucide-react';

export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    try {
      await api.requestPasswordReset(email);
      setSuccess(true);
    } catch {
      setError(t('forgotPassword.genericError', 'Ocorreu um erro. Tente novamente mais tarde.'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-background via-background to-muted/30 p-4">
      <div className="w-full max-w-md">
        <div className="mb-8 flex flex-col items-center text-center">
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary text-primary-foreground">
            <BookOpen className="h-7 w-7" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight">reformu.lat</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('forgotPassword.professionalPlatform', 'Plataforma profissional de escrita de livros')}
          </p>
        </div>

        <Card className="border-border/50 shadow-lg">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-xl">{t('forgotPassword.forgotPasswordTitle', 'Esqueceu a senha?')}</CardTitle>
            <CardDescription>
              {t('forgotPassword.emailInstructions', 'Informe seu e-mail e enviaremos um link para redefinir sua senha.')}
            </CardDescription>
          </CardHeader>

          {success ? (
            <CardContent>
              <div className="flex flex-col items-center gap-3 py-4 text-center">
                <CheckCircle2 className="h-10 w-10 text-primary" />
                <p className="font-medium">{t('forgotPassword.checkYourInbox', 'Verifique sua caixa de entrada')}</p>
                <p className="text-sm text-muted-foreground">
                  {t('forgotPassword.accountAssociatedMessage', 'Se houver uma conta associada a {{email}}, você receberá um e-mail com o link para redefinir sua senha em breve.', { email })}
                </p>
              </div>
            </CardContent>
          ) : (
            <form onSubmit={handleSubmit}>
              <CardContent className="space-y-4">
                {error && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email">{t('forgotPassword.email', 'E-mail')}</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder={t('forgotPassword.emailPlaceholder', 'voce@exemplo.com')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    disabled={isLoading}
                  />
                </div>
              </CardContent>

              <CardFooter className="flex flex-col gap-4">
                <Button
                  type="submit"
                  className="w-full"
                  disabled={isLoading || !email}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('forgotPassword.sending', 'Enviando...')}
                    </>
                  ) : (
                    t('forgotPassword.sendResetLink', 'Enviar link de redefinição')
                  )}
                </Button>
              </CardFooter>
            </form>
          )}
        </Card>

        <div className="mt-4 flex justify-center">
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('forgotPassword.backToLogin', 'Voltar para o login')}
          </Link>
        </div>
      </div>
    </div>
  );
}
